import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
  getPresupuestos, createPresupuesto, getPresupuesto, updatePresupuesto,
  deletePresupuesto, calcularCuotasPresupuesto, confirmarPresupuesto,
  cerrarPresupuesto, getPresupuestoCuotas, seleccionarPago,
  confirmarPago, confirmarPagosMultiples, sendRecordatorio,
  getBizumConfig, updateBizumConfig, getUsers, getEvents
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Trash2, Calculator, CheckCircle2, Clock, FileText, Users,
  Receipt, ArrowLeft, Send, Copy, Banknote, Smartphone, Eye,
  Edit, Lock, XCircle, Bell, ChevronDown, ChevronUp
} from 'lucide-react';

const STATUS_STYLES = {
  borrador: { label: 'Borrador', color: 'bg-stone-100 text-stone-700', icon: Edit },
  confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  cerrado: { label: 'Cerrado', color: 'bg-emerald-100 text-emerald-700', icon: Lock },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const PAGO_STYLES = {
  sin_respuesta: { label: 'Sin respuesta', color: 'bg-stone-100 text-stone-600' },
  pendiente_efectivo: { label: 'Pend. Efectivo', color: 'bg-amber-100 text-amber-700' },
  pendiente_bizum: { label: 'Pend. Bizum', color: 'bg-blue-100 text-blue-700' },
  pagado: { label: 'Pagado', color: 'bg-emerald-100 text-emerald-700' },
  exento: { label: 'Exento', color: 'bg-purple-100 text-purple-700' }
};

export default function Presupuestos() {
  const { hasPermission, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [cuotasData, setCuotasData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [detailTab, setDetailTab] = useState('partidas');

  // Create/Edit form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    nombre: '', descripcion: '', evento_id: null, fecha_evento: '',
    metodo_financiacion: 'reparto_participantes', aportacion_grupo: 0,
    partidas: [{ concepto: '', cantidad: 1, precio_unitario: 0 }],
    participantes_ids: [], reglas_cuota: []
  });

  // Payment modals
  const [showPaySelect, setShowPaySelect] = useState(null);
  const [showConfirmPay, setShowConfirmPay] = useState(null);
  const [confirmNotes, setConfirmNotes] = useState('');
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [showBizumInfo, setShowBizumInfo] = useState(null);
  const [showConfirmBudget, setShowConfirmBudget] = useState(false);

  // Bizum config
  const [showBizumConfig, setShowBizumConfig] = useState(false);
  const [bizumForm, setBizumForm] = useState({ numero_receptor: '', nombre_titular: '', notas_adicionales: '', bizum_activo: true, efectivo_activo: true });

  useEffect(() => { fetchList(); }, []);

  // Handle URL params for deep linking (e.g., from notifications)
  useEffect(() => {
    const presId = searchParams.get('id');
    const action = searchParams.get('action');
    if (presId && !loading && presupuestos.length > 0) {
      openDetail(presId).then(() => {
        if (action === 'pay' && user) {
          // Auto-open payment method selector for current user
          setTimeout(() => setShowPaySelect(user.id), 500);
        }
      });
      // Clear params after handling
      setSearchParams({}, { replace: true });
    }
  }, [loading, presupuestos, searchParams]);

  const fetchList = async () => {
    try {
      const [presRes, usersRes, eventsRes] = await Promise.all([
        getPresupuestos(), getUsers(), getEvents()
      ]);
      setPresupuestos(presRes.data);
      setUsers(usersRes.data);
      setEvents(eventsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openDetail = async (id) => {
    try {
      const [detailRes, cuotasRes] = await Promise.all([
        getPresupuesto(id), getPresupuestoCuotas(id)
      ]);
      setDetail(detailRes.data);
      setCuotasData(cuotasRes.data);
      setSelectedId(id);
      setView('detail');
      setDetailTab(detailRes.data.estado === 'borrador' ? 'partidas' : 'cuotas');
    } catch (err) { toast.error('Error cargando detalle'); }
  };

  const refreshDetail = async () => {
    if (!selectedId) return;
    const [detailRes, cuotasRes] = await Promise.all([
      getPresupuesto(selectedId), getPresupuestoCuotas(selectedId)
    ]);
    setDetail(detailRes.data);
    setCuotasData(cuotasRes.data);
  };

  const resetForm = () => setForm({
    nombre: '', descripcion: '', evento_id: null, fecha_evento: '',
    metodo_financiacion: 'reparto_participantes', aportacion_grupo: 0,
    partidas: [{ concepto: '', cantidad: 1, precio_unitario: 0 }],
    participantes_ids: [], reglas_cuota: []
  });

  // --- Partidas ---
  const addPartida = () => setForm({ ...form, partidas: [...form.partidas, { concepto: '', cantidad: 1, precio_unitario: 0 }] });
  const removePartida = (idx) => setForm({ ...form, partidas: form.partidas.filter((_, i) => i !== idx) });
  const updatePartida = (idx, field, value) => {
    const p = [...form.partidas];
    p[idx] = { ...p[idx], [field]: field === 'concepto' ? value : parseFloat(value) || 0 };
    p[idx].subtotal = p[idx].cantidad * p[idx].precio_unitario;
    setForm({ ...form, partidas: p });
  };

  // --- Reglas ---
  const addRegla = () => setForm({
    ...form, reglas_cuota: [...form.reglas_cuota,
      { orden: form.reglas_cuota.length, criterio: 'categoria', campo: '', operador: 'igual_a', valor: '', tipo_descuento: 'porcentaje', valor_descuento: 30 }]
  });
  const removeRegla = (idx) => setForm({ ...form, reglas_cuota: form.reglas_cuota.filter((_, i) => i !== idx) });
  const updateRegla = (idx, field, value) => {
    const r = [...form.reglas_cuota];
    r[idx] = { ...r[idx], [field]: value };
    setForm({ ...form, reglas_cuota: r });
  };

  // --- Participants ---
  const toggleParticipant = (uid) => {
    const ids = form.participantes_ids.includes(uid)
      ? form.participantes_ids.filter(id => id !== uid)
      : [...form.participantes_ids, uid];
    setForm({ ...form, participantes_ids: ids });
  };
  const selectAll = () => setForm({ ...form, participantes_ids: users.filter(u => u.is_active).map(u => u.id) });
  const selectNone = () => setForm({ ...form, participantes_ids: [] });

  const totalPartidas = form.partidas.reduce((s, p) => s + (p.cantidad || 0) * (p.precio_unitario || 0), 0);

  // --- CRUD ---
  const handleCreate = async () => {
    if (!form.nombre || form.partidas.length === 0) return toast.error('Nombre y al menos 1 partida requeridos');
    try {
      const res = await createPresupuesto(form);
      toast.success('Presupuesto creado como borrador');
      setShowCreate(false);
      resetForm();
      fetchList();
      openDetail(res.data.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
  };

  const handleSaveEdit = async () => {
    try {
      await updatePresupuesto(selectedId, form);
      toast.success('Presupuesto actualizado');
      refreshDetail();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    try {
      await deletePresupuesto(id);
      toast.success('Presupuesto eliminado');
      setView('list');
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleCalculate = async () => {
    try {
      const res = await calcularCuotasPresupuesto(selectedId);
      setPreviewData(res.data);
      toast.success('Cálculo realizado');
    } catch (err) { toast.error('Error al calcular'); }
  };

  const handleConfirm = async () => {
    try {
      const res = await confirmarPresupuesto(selectedId);
      toast.success(res.data.message);
      setShowConfirmBudget(false);
      refreshDetail();
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al confirmar'); }
  };

  const handleClose = async () => {
    if (!window.confirm('¿Cerrar presupuesto? No se podrán confirmar más pagos.')) return;
    try {
      await cerrarPresupuesto(selectedId);
      toast.success('Presupuesto cerrado');
      refreshDetail();
      fetchList();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // --- Payments ---
  const handleSelectPay = async (method) => {
    try {
      const res = await seleccionarPago(selectedId, showPaySelect, { metodo: method });
      toast.success(res.data.message);
      if (method === 'bizum' && res.data.bizum_info) {
        setShowBizumInfo(res.data.bizum_info);
      }
      setShowPaySelect(null);
      refreshDetail();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleConfirmPayment = async () => {
    try {
      const res = await confirmarPago(selectedId, showConfirmPay, { notas: confirmNotes });
      toast.success(res.data.message);
      setShowConfirmPay(null);
      setConfirmNotes('');
      refreshDetail();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleBulkConfirm = async () => {
    if (selectedForBulk.length === 0) return toast.error('Selecciona participantes');
    try {
      const res = await confirmarPagosMultiples(selectedId, { user_ids: selectedForBulk });
      toast.success(res.data.message);
      setSelectedForBulk([]);
      refreshDetail();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleReminder = async () => {
    try {
      const res = await sendRecordatorio(selectedId);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // --- Bizum Config ---
  const openBizumConfig = async () => {
    try {
      const res = await getBizumConfig();
      setBizumForm(res.data);
      setShowBizumConfig(true);
    } catch { setShowBizumConfig(true); }
  };

  const saveBizumConfig = async () => {
    try {
      await updateBizumConfig(bizumForm);
      toast.success('Configuración Bizum guardada');
      setShowBizumConfig(false);
    } catch (err) { toast.error('Error al guardar'); }
  };

  const canEdit = hasPermission('finanzas', 'editar');
  const canCreate = hasPermission('finanzas', 'crear');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  // ========= DETAIL VIEW =========
  if (view === 'detail' && detail) {
    const status = STATUS_STYLES[detail.estado] || STATUS_STYLES.borrador;
    const StatusIcon = status.icon;
    const participantes = cuotasData?.participantes || [];
    const isEditable = detail.estado === 'borrador';
    const recaudado = cuotasData?.total_recaudado || 0;
    const pendiente = cuotasData?.total_pendiente || 0;
    const total = detail.total_presupuesto || 0;
    const progress = total > 0 ? (recaudado / (recaudado + pendiente)) * 100 : 0;

    return (
      <div className="space-y-6" data-testid="presupuesto-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setView('list'); setDetail(null); }} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-stone-800">{detail.nombre}</h1>
                <Badge className={status.color}><StatusIcon className="w-3 h-3 mr-1" />{status.label}</Badge>
              </div>
              {detail.descripcion && <p className="text-stone-500 text-sm mt-1">{detail.descripcion}</p>}
              {detail.evento_id && (() => {
                const ev = events.find(e => e.id === detail.evento_id);
                return ev ? <p className="text-xs text-blue-500 mt-1">Vinculado a: {ev.title}</p> : null;
              })()}
            </div>
          </div>
          <div className="flex gap-2">
            {isEditable && canEdit && (
              <Button variant="outline" className="text-red-600" onClick={() => handleDelete(detail.id)} data-testid="delete-budget-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Eliminar
              </Button>
            )}
            {isEditable && canEdit && (
              <Button onClick={() => setShowConfirmBudget(true)} data-testid="confirm-budget-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar Presupuesto
              </Button>
            )}
            {detail.estado === 'confirmado' && canEdit && (
              <Button variant="outline" onClick={handleClose} data-testid="close-budget-btn">
                <Lock className="w-4 h-4 mr-1" /> Cerrar
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4"><p className="text-xs text-stone-500">Total Presupuesto</p><p className="text-xl font-bold text-stone-800">{total.toFixed(2)}€</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-stone-500">Recaudado</p><p className="text-xl font-bold text-emerald-600">{recaudado.toFixed(2)}€</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-stone-500">Pendiente</p><p className="text-xl font-bold text-amber-600">{pendiente.toFixed(2)}€</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-stone-500">Participantes</p><p className="text-xl font-bold text-stone-800">{participantes.length}</p></CardContent></Card>
        </div>

        {detail.estado !== 'borrador' && (
          <div data-testid="collection-progress">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-stone-500">Recaudación</span>
              <span className="font-medium">{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={detailTab} onValueChange={setDetailTab}>
          <TabsList data-testid="detail-tabs">
            <TabsTrigger value="partidas"><FileText className="w-4 h-4 mr-1" /> Partidas</TabsTrigger>
            <TabsTrigger value="participantes"><Users className="w-4 h-4 mr-1" /> Participantes</TabsTrigger>
            <TabsTrigger value="cuotas"><Receipt className="w-4 h-4 mr-1" /> Gestión Cuotas</TabsTrigger>
          </TabsList>

          {/* Partidas Tab */}
          <TabsContent value="partidas">
            <Card>
              <CardHeader><CardTitle className="text-lg">Partidas de Gasto</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-stone-500">
                      <th className="pb-3 font-medium">Concepto</th>
                      <th className="pb-3 font-medium text-right">Cantidad</th>
                      <th className="pb-3 font-medium text-right">Precio Unit.</th>
                      <th className="pb-3 font-medium text-right">Subtotal</th>
                    </tr></thead>
                    <tbody>
                      {(detail.partidas || []).map((p, i) => (
                        <tr key={i} className="border-b border-stone-100">
                          <td className="py-3 text-stone-800">{p.concepto}</td>
                          <td className="py-3 text-right text-stone-600">{p.cantidad}</td>
                          <td className="py-3 text-right text-stone-600">{p.precio_unitario?.toFixed(2)}€</td>
                          <td className="py-3 text-right font-medium text-stone-800">{p.subtotal?.toFixed(2)}€</td>
                        </tr>
                      ))}
                      <tr className="font-bold">
                        <td className="pt-3" colSpan={3}>TOTAL</td>
                        <td className="pt-3 text-right">{total.toFixed(2)}€</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-stone-50 text-sm space-y-1">
                  <p>Financiación: <strong className="capitalize">{detail.metodo_financiacion?.replace('_', ' ')}</strong></p>
                  {detail.aportacion_grupo > 0 && <p>Aportación grupo: <strong>{detail.aportacion_grupo?.toFixed(2)}€</strong></p>}
                  <p>Importe a repartir: <strong>{detail.importe_a_repartir?.toFixed(2)}€</strong></p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Participantes Tab */}
          <TabsContent value="participantes">
            <Card>
              <CardHeader><CardTitle className="text-lg">Participantes y Reglas</CardTitle></CardHeader>
              <CardContent>
                {(detail.reglas_cuota || []).length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-sm font-medium text-stone-700">Reglas de descuento aplicadas:</p>
                    {detail.reglas_cuota.map((r, i) => (
                      <div key={i} className="text-xs p-2 bg-stone-50 rounded flex items-center gap-2">
                        <Badge variant="outline">{r.criterio}</Badge>
                        <span>{r.operador} "{r.valor}"</span>
                        <span className="ml-auto font-medium">{r.tipo_descuento === 'exento' ? 'Exento' : `-${r.valor_descuento}%`}</span>
                      </div>
                    ))}
                  </div>
                )}
                {participantes.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-stone-500">
                      <th className="pb-2 font-medium">Nombre</th>
                      <th className="pb-2 font-medium">Categoría</th>
                      <th className="pb-2 font-medium">Regla</th>
                      <th className="pb-2 font-medium text-right">%</th>
                      <th className="pb-2 font-medium text-right">Cuota</th>
                    </tr></thead>
                    <tbody>
                      {participantes.map(p => (
                        <tr key={p.user_id} className="border-b border-stone-100">
                          <td className="py-2 text-stone-800">{p.nombre_completo}</td>
                          <td className="py-2"><Badge variant="outline" className="text-xs">{p.categoria || '-'}</Badge></td>
                          <td className="py-2 text-xs text-stone-500">{p.regla_aplicada}</td>
                          <td className="py-2 text-right">{p.porcentaje_pago}%</td>
                          <td className="py-2 text-right font-medium">{p.cuota_calculada?.toFixed(2)}€</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-stone-400 text-center py-8">No hay participantes. {isEditable ? 'Edita el presupuesto para añadirlos.' : ''}</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cuotas Management Tab */}
          <TabsContent value="cuotas">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Gestión de Cuotas</CardTitle>
                  <div className="flex gap-2">
                    {canEdit && detail.estado === 'confirmado' && (
                      <>
                        <Button variant="outline" size="sm" onClick={handleReminder} data-testid="send-reminder-btn">
                          <Bell className="w-4 h-4 mr-1" /> Recordatorio
                        </Button>
                        {selectedForBulk.length > 0 && (
                          <Button size="sm" onClick={handleBulkConfirm} data-testid="bulk-confirm-btn">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar {selectedForBulk.length} seleccionados
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {participantes.length === 0 ? (
                  <p className="text-stone-400 text-center py-8">Presupuesto no confirmado o sin participantes</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-left text-stone-500">
                        {canEdit && detail.estado === 'confirmado' && <th className="pb-2 w-8"></th>}
                        <th className="pb-2 font-medium">Nombre</th>
                        <th className="pb-2 font-medium text-right">Cuota</th>
                        <th className="pb-2 font-medium">Método</th>
                        <th className="pb-2 font-medium">Estado</th>
                        <th className="pb-2 font-medium text-right">Acciones</th>
                      </tr></thead>
                      <tbody>
                        {participantes.map(p => {
                          const ps = PAGO_STYLES[p.estado_pago] || PAGO_STYLES.sin_respuesta;
                          const isOwn = user?.id === p.user_id;
                          const canSelectMethod = (isOwn || canEdit) && detail.estado === 'confirmado' && !['pagado', 'exento'].includes(p.estado_pago);
                          const canConfirmThis = canEdit && detail.estado === 'confirmado' && !['pagado', 'exento', 'sin_respuesta'].includes(p.estado_pago);
                          return (
                            <tr key={p.user_id} className="border-b border-stone-100 hover:bg-stone-50">
                              {canEdit && detail.estado === 'confirmado' && (
                                <td className="py-2">
                                  {!['pagado', 'exento'].includes(p.estado_pago) && (
                                    <Checkbox
                                      checked={selectedForBulk.includes(p.user_id)}
                                      onCheckedChange={(checked) => {
                                        setSelectedForBulk(prev => checked ? [...prev, p.user_id] : prev.filter(id => id !== p.user_id));
                                      }}
                                    />
                                  )}
                                </td>
                              )}
                              <td className="py-2 text-stone-800">{p.nombre_completo}</td>
                              <td className="py-2 text-right font-medium">{p.cuota_calculada?.toFixed(2)}€</td>
                              <td className="py-2">
                                {p.metodo_pago_seleccionado ? (
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {p.metodo_pago_seleccionado === 'bizum' ? <Smartphone className="w-3 h-3 mr-1" /> : <Banknote className="w-3 h-3 mr-1" />}
                                    {p.metodo_pago_seleccionado}
                                  </Badge>
                                ) : <span className="text-stone-400 text-xs">-</span>}
                              </td>
                              <td className="py-2"><Badge className={`text-xs ${ps.color}`}>{ps.label}</Badge></td>
                              <td className="py-2 text-right">
                                <div className="flex gap-1 justify-end">
                                  {canSelectMethod && (
                                    <Button variant="outline" size="sm" onClick={() => setShowPaySelect(p.user_id)} data-testid={`select-pay-${p.user_id}`}>
                                      Pagar
                                    </Button>
                                  )}
                                  {canConfirmThis && (
                                    <Button size="sm" onClick={() => { setShowConfirmPay(p.user_id); setConfirmNotes(''); }} data-testid={`confirm-pay-${p.user_id}`}>
                                      <CheckCircle2 className="w-3 h-3 mr-1" /> Confirmar
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Payment Method Selection Modal */}
        <Dialog open={!!showPaySelect} onOpenChange={() => setShowPaySelect(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Seleccionar Método de Pago</DialogTitle></DialogHeader>
            <div className="space-y-3 py-4">
              <Button variant="outline" className="w-full h-16 text-lg justify-start gap-4" onClick={() => handleSelectPay('efectivo')} data-testid="pay-cash-btn">
                <Banknote className="w-8 h-8 text-emerald-600" /> Pagar en Efectivo
              </Button>
              <Button variant="outline" className="w-full h-16 text-lg justify-start gap-4" onClick={() => handleSelectPay('bizum')} data-testid="pay-bizum-btn">
                <Smartphone className="w-8 h-8 text-blue-600" /> Pagar por Bizum
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bizum Info Modal */}
        <Dialog open={!!showBizumInfo} onOpenChange={() => setShowBizumInfo(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Datos para Bizum</DialogTitle></DialogHeader>
            {showBizumInfo && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-stone-500">Número:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-lg">{showBizumInfo.numero || 'No configurado'}</span>
                      <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(showBizumInfo.numero); toast.success('Número copiado'); }} data-testid="copy-bizum-num">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between"><span className="text-sm text-stone-500">Titular:</span><span className="font-medium">{showBizumInfo.titular || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-stone-500">Importe:</span><span className="font-bold text-lg text-blue-700">{showBizumInfo.importe?.toFixed(2)}€</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-stone-500">Concepto:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{showBizumInfo.concepto}</span>
                      <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(showBizumInfo.concepto); toast.success('Concepto copiado'); }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                {showBizumInfo.notas && <p className="text-sm text-stone-500">{showBizumInfo.notas}</p>}
              </div>
            )}
            <DialogFooter><Button onClick={() => setShowBizumInfo(null)}>Entendido</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Payment Modal (Treasurer) */}
        <Dialog open={!!showConfirmPay} onOpenChange={() => setShowConfirmPay(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Confirmar Pago Recibido</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-stone-600">¿Confirmar que se ha recibido el pago de este participante?</p>
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea value={confirmNotes} onChange={e => setConfirmNotes(e.target.value)} rows={2} placeholder="Notas sobre el pago..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmPay(null)}>Cancelar</Button>
              <Button onClick={handleConfirmPayment} data-testid="confirm-payment-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar Pago</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Budget Modal */}
        <Dialog open={showConfirmBudget} onOpenChange={setShowConfirmBudget}>
          <DialogContent>
            <DialogHeader><DialogTitle>Confirmar Presupuesto</DialogTitle></DialogHeader>
            <div className="space-y-3 py-4">
              <p className="text-sm text-stone-600">Al confirmar:</p>
              <ul className="text-sm space-y-1 text-stone-700 list-disc ml-4">
                <li>Se generarán cuotas individuales</li>
                <li>Se notificará a los participantes</li>
                <li>El presupuesto no podrá editarse</li>
              </ul>
              <div className="p-3 bg-stone-50 rounded-lg text-sm">
                <p>Presupuesto: <strong>{detail.nombre}</strong></p>
                <p>Total: <strong>{total.toFixed(2)}€</strong></p>
                <p>A repartir: <strong>{detail.importe_a_repartir?.toFixed(2)}€</strong></p>
                <p>Participantes: <strong>{detail.participantes_ids?.length || 0}</strong></p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmBudget(false)}>Cancelar</Button>
              <Button onClick={handleConfirm} data-testid="confirm-budget-final-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ========= LIST VIEW =========
  return (
    <div className="space-y-6" data-testid="presupuestos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Presupuestos</h1>
          <p className="text-stone-500 text-sm">Gestión de presupuestos y cuotas por evento</p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={openBizumConfig} data-testid="bizum-config-btn">
              <Smartphone className="w-4 h-4 mr-2" /> Config Bizum
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => { setShowCreate(true); resetForm(); }} data-testid="create-budget-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Presupuesto
            </Button>
          )}
        </div>
      </div>

      {presupuestos.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-stone-400">
          <Receipt className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No hay presupuestos creados</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {presupuestos.map(p => {
            const st = STATUS_STYLES[p.estado] || STATUS_STYLES.borrador;
            const StIcon = st.icon;
            const progress = p.total_presupuesto > 0 ? ((p.total_recaudado || 0) / p.total_presupuesto) * 100 : 0;
            return (
              <Card key={p.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(p.id)} data-testid={`budget-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-stone-800">{p.nombre}</h3>
                        <Badge className={st.color}><StIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                      </div>
                      {p.descripcion && <p className="text-sm text-stone-500 truncate">{p.descripcion}</p>}
                      {p.evento_id && (() => {
                        const ev = events.find(e => e.id === p.evento_id);
                        return ev ? <p className="text-xs text-blue-500 mt-1">Evento: {ev.title}</p> : null;
                      })()}
                      <div className="flex gap-4 mt-2 text-xs text-stone-400">
                        <span>{p.total_presupuesto?.toFixed(2)}€ total</span>
                        <span>{(p.total_recaudado || 0).toFixed(2)}€ recaudado</span>
                        <span>{p.numero_participantes || 0} participantes</span>
                      </div>
                    </div>
                    {p.estado !== 'borrador' && (
                      <div className="w-24">
                        <Progress value={progress} className="h-2" />
                        <p className="text-xs text-stone-400 text-center mt-1">{progress.toFixed(0)}%</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Budget Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo Presupuesto</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Fiesta Patronal 2026" data-testid="budget-nombre" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Descripción</Label>
                <Textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Vincular a Evento</Label>
                <Select value={form.evento_id || '_none'} onValueChange={v => {
                  if (v === '_none') {
                    setForm({ ...form, evento_id: null, fecha_evento: '' });
                  } else {
                    const ev = events.find(e => e.id === v);
                    setForm({ ...form, evento_id: v, fecha_evento: ev?.start_date?.split('T')[0] || '' });
                  }
                }}>
                  <SelectTrigger data-testid="budget-evento"><SelectValue placeholder="Seleccionar evento..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin evento vinculado</SelectItem>
                    {events.map(ev => (
                      <SelectItem key={ev.id} value={ev.id}>{ev.title} {ev.start_date ? `(${ev.start_date.split('T')[0]})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Financiación</Label>
                <Select value={form.metodo_financiacion} onValueChange={v => setForm({ ...form, metodo_financiacion: v })}>
                  <SelectTrigger data-testid="budget-financiacion"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reparto_participantes">Repartir entre participantes</SelectItem>
                    <SelectItem value="fondos_grupo">Fondos del grupo</SelectItem>
                    <SelectItem value="mixto">Mixto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.metodo_financiacion === 'mixto' && (
                <div className="space-y-2">
                  <Label>Aportación del grupo (€)</Label>
                  <Input type="number" min="0" step="0.01" value={form.aportacion_grupo} onChange={e => setForm({ ...form, aportacion_grupo: parseFloat(e.target.value) || 0 })} data-testid="budget-aportacion" />
                </div>
              )}
            </div>

            {/* Partidas */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold">Partidas de Gasto</Label>
                <Button variant="outline" size="sm" onClick={addPartida} data-testid="add-partida-btn">
                  <Plus className="w-3 h-3 mr-1" /> Partida
                </Button>
              </div>
              <div className="space-y-2">
                {form.partidas.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input className="flex-1" placeholder="Concepto" value={p.concepto} onChange={e => updatePartida(i, 'concepto', e.target.value)} data-testid={`partida-concepto-${i}`} />
                    <Input className="w-20" type="number" min="1" value={p.cantidad} onChange={e => updatePartida(i, 'cantidad', e.target.value)} />
                    <Input className="w-28" type="number" min="0" step="0.01" value={p.precio_unitario} onChange={e => updatePartida(i, 'precio_unitario', e.target.value)} />
                    <span className="w-20 text-right text-sm font-medium">{(p.cantidad * p.precio_unitario).toFixed(2)}€</span>
                    {form.partidas.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removePartida(i)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="text-right font-bold text-stone-800 pr-12">Total: {totalPartidas.toFixed(2)}€</div>
              </div>
            </div>

            {/* Participants */}
            {form.metodo_financiacion !== 'fondos_grupo' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Participantes ({form.participantes_ids.length})</Label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">Todos</Button>
                    <Button variant="outline" size="sm" onClick={selectNone}>Ninguno</Button>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {users.filter(u => u.is_active).map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-stone-50 cursor-pointer text-sm">
                      <Checkbox
                        checked={form.participantes_ids.includes(u.id)}
                        onCheckedChange={() => toggleParticipant(u.id)}
                      />
                      <span>{u.full_name}</span>
                      <span className="text-xs text-stone-400 ml-auto">{u.categoria || 'adulto'}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Rules */}
            {form.metodo_financiacion !== 'fondos_grupo' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Reglas de Descuento</Label>
                  <Button variant="outline" size="sm" onClick={addRegla} data-testid="add-regla-btn">
                    <Plus className="w-3 h-3 mr-1" /> Regla
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.reglas_cuota.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 border rounded-lg bg-stone-50 text-sm">
                      <Select value={r.criterio} onValueChange={v => updateRegla(i, 'criterio', v)}>
                        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="categoria">Categoría</SelectItem>
                          <SelectItem value="rol">Rol</SelectItem>
                          <SelectItem value="edad">Edad</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={r.operador} onValueChange={v => updateRegla(i, 'operador', v)}>
                        <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="igual_a">Igual a</SelectItem>
                          <SelectItem value="menor_de">Menor de</SelectItem>
                          <SelectItem value="mayor_de">Mayor de</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input className="w-28" placeholder="Valor" value={r.valor} onChange={e => updateRegla(i, 'valor', e.target.value)} />
                      <Select value={r.tipo_descuento} onValueChange={v => updateRegla(i, 'tipo_descuento', v)}>
                        <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="porcentaje">% Descuento</SelectItem>
                          <SelectItem value="exento">Exento</SelectItem>
                        </SelectContent>
                      </Select>
                      {r.tipo_descuento === 'porcentaje' && (
                        <Input className="w-20" type="number" min="0" max="100" value={r.valor_descuento} onChange={e => updateRegla(i, 'valor_descuento', parseFloat(e.target.value) || 0)} />
                      )}
                      <Button variant="ghost" size="icon" onClick={() => removeRegla(i)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} data-testid="submit-budget-btn">Guardar Borrador</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bizum Config Dialog */}
      <Dialog open={showBizumConfig} onOpenChange={setShowBizumConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Configuración Bizum</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número receptor Bizum</Label>
              <Input value={bizumForm.numero_receptor} onChange={e => setBizumForm({ ...bizumForm, numero_receptor: e.target.value })} placeholder="+34XXXXXXXXX" data-testid="bizum-numero" />
            </div>
            <div className="space-y-2">
              <Label>Nombre titular</Label>
              <Input value={bizumForm.nombre_titular} onChange={e => setBizumForm({ ...bizumForm, nombre_titular: e.target.value })} placeholder="Nombre del titular" data-testid="bizum-titular" />
            </div>
            <div className="space-y-2">
              <Label>Notas adicionales</Label>
              <Textarea value={bizumForm.notas_adicionales} onChange={e => setBizumForm({ ...bizumForm, notas_adicionales: e.target.value })} rows={2} placeholder="Instrucciones adicionales para el pago" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={bizumForm.bizum_activo} onCheckedChange={v => setBizumForm({ ...bizumForm, bizum_activo: v })} />
                Bizum activo
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={bizumForm.efectivo_activo} onCheckedChange={v => setBizumForm({ ...bizumForm, efectivo_activo: v })} />
                Efectivo activo
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBizumConfig(false)}>Cancelar</Button>
            <Button onClick={saveBizumConfig} data-testid="save-bizum-config-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
